import type { HTMLContent, JSONContent } from "@tiptap/core";
import type { Editor } from "@/components/ui/shadcn-io/editor";
import {
  EditorFloatingMenu,
  EditorNodeBulletList,
  EditorNodeCode,
  EditorNodeHeading1,
  EditorNodeQuote,
  EditorNodeTable,
  EditorProvider,
  EditorTableColumnAfter,
  EditorTableColumnBefore,
  EditorTableColumnDelete,
  EditorTableColumnMenu,
  EditorTableDelete,
  EditorTableFix,
  EditorTableGlobalMenu,
  EditorTableHeaderColumnToggle,
  EditorTableHeaderRowToggle,
  EditorTableMenu,
  EditorTableMergeCells,
  EditorTableRowAfter,
  EditorTableRowBefore,
  EditorTableRowDelete,
  EditorTableRowMenu,
  EditorTableSplitCell,
  EditorWordsAndCharactersCounter,
  EditorToolbar,
} from "@/components/ui/shadcn-io/editor";

import { TooltipProvider } from "@/components/ui/tooltip";
import { Markdown } from "tiptap-markdown";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";

interface TipTapEditorProps {
  // Old API (for backward compatibility)
  fileName?: string;
  summaryHTML?: HTMLContent;
  onUpdateCallback?: (html: string) => void;
  /**
   * Serialization format for the old API. "markdown" loads `summaryHTML` as
   * Markdown and hands Markdown back to `onUpdateCallback`; "html" (the
   * default) keeps the original HTML round-trip for legacy documents.
   */
  format?: "html" | "markdown";
  // New API (for read-only display with JSONContent)
  content?: JSONContent;
  editable?: boolean;
  onChange?: (content: JSONContent) => void;
  className?: string;
}

const TipTapEditor = ({
  fileName,
  summaryHTML,
  onUpdateCallback,
  format = "html",
  content,
  editable = true,
  onChange,
  className,
}: TipTapEditorProps) => {
  // Determine which API is being used
  const useNewAPI = content !== undefined;
  const isMarkdown = !useNewAPI && format === "markdown";

  // The Markdown extension patches setContent/insertContent, so it must be
  // loaded for the initial `content` string to be parsed as Markdown at all.
  const extraExtensions = useMemo(
    () => (isMarkdown ? [Markdown.configure({ html: true, transformPastedText: true })] : []),
    [isMarkdown]
  );

  const [internalContent, setInternalContent] = useState<HTMLContent | JSONContent>(
    useNewAPI ? content : (summaryHTML as HTMLContent)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleUpdate = useCallback(({ editor }: { editor: Editor }) => {
    if (!editable) return; // Don't update if read-only

    setHasUnsavedChanges(true);

    if (useNewAPI) {
      // New API: Return JSONContent
      const json = editor.getJSON();
      setInternalContent(json);
      onChange?.(json);
    } else {
      // Old API: hand back the same format we were given, so a save never
      // silently rewrites a Markdown document into HTML.
      const html = isMarkdown
        ? (editor.storage as unknown as { markdown: { getMarkdown: () => string } }).markdown.getMarkdown()
        : editor.getHTML();
      setInternalContent(html);

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer for 2 seconds
      debounceTimerRef.current = setTimeout(async () => {
        setIsSaving(true);
        try {
          await onUpdateCallback!(html);
          setIsSaving(false);
          setHasUnsavedChanges(false);
        } catch (error) {
          console.error("Error saving editor state:", error);
          setIsSaving(false);
          // Keep hasUnsavedChanges as true when there's an error
          setHasUnsavedChanges(true);
        }
      }, 2000);
    }
  }, [editable, useNewAPI, isMarkdown, onChange, onUpdateCallback]);

  // Update content when prop changes (for new API)
  useEffect(() => {
    if (useNewAPI && content !== undefined) {
      setInternalContent(content);
    }
  }, [useNewAPI, content]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <TooltipProvider>
      <div className={`relative h-full w-full flex flex-col ${className || ""}`}>
        <EditorProvider
          className="flex-1 overflow-y-auto rounded-lg border bg-background mt-20 pt-0.5 px-6"
          extensions={extraExtensions}
          content={internalContent}
          onUpdate={handleUpdate}
          editable={editable}
          placeholder="התחל לכתוב..."
        >
          {!useNewAPI && (
            <EditorToolbar isSaving={isSaving} hasUnsavedChanges={hasUnsavedChanges} fileName={fileName!} />
          )}
          <div className="flex-1 overflow-y-auto p-4">
            {editable && (
              <>
                <EditorFloatingMenu>
                  <EditorNodeHeading1 />
                  <EditorNodeBulletList />
                  <EditorNodeQuote />
                  <EditorNodeCode />
                  <EditorNodeTable />
                </EditorFloatingMenu>
                <EditorTableMenu>
                  <EditorTableColumnMenu>
                    <EditorTableColumnBefore />
                    <EditorTableColumnAfter />
                    <EditorTableColumnDelete />
                  </EditorTableColumnMenu>
                  <EditorTableRowMenu>
                    <EditorTableRowBefore />
                    <EditorTableRowAfter />
                    <EditorTableRowDelete />
                  </EditorTableRowMenu>
                  <EditorTableGlobalMenu>
                    <EditorTableHeaderColumnToggle />
                    <EditorTableHeaderRowToggle />
                    <EditorTableDelete />
                    <EditorTableMergeCells />
                    <EditorTableSplitCell />
                    <EditorTableFix />
                  </EditorTableGlobalMenu>
                </EditorTableMenu>
                <EditorWordsAndCharactersCounter />
              </>
            )}
          </div>
        </EditorProvider>
      </div>
    </TooltipProvider>
  );
};

export default TipTapEditor;
